import {
  ACCEPTABLE_PREFILLED_CARD_DATA_QUERY_STRINGS,
  OPAQUE_TOKEN_CACHE_KEY,
  PREFILLED_CARD_DATA_CACHE_KEY,
  REFERRAL_DATA_CACHE_KEY,
  type ReferralDataQueryStringKey,
} from '@constants/referral';
import { analytics } from '@modules/analytics';

import { logger } from './logger';

/**
 * Parse the query parameters to prefill the carc
 * @param query the query parsed from the url
 * @returns
 */
export function parseAndCachePrefilledCardData(
  query: Record<string, string | string[] | undefined>,
): Record<string, string> | undefined {
  const dlTag = query._dl;
  if (Array.isArray(dlTag)) {
    return;
  }

  const existingCache = getCachedPrefilledCardData() ?? {};

  const prefilledCardData = Object.fromEntries(
    Object.entries(query)
      .filter(([entryKey]) =>
        ACCEPTABLE_PREFILLED_CARD_DATA_QUERY_STRINGS.includes(entryKey as ReferralDataQueryStringKey),
      )
      .map(([key, value]) => {
        const queryValue = Array.isArray(value) ? value[0] : value || '';

        return [key, queryValue];
      }),
  ) as Record<ReferralDataQueryStringKey, string>;

  window.localStorage.setItem(
    PREFILLED_CARD_DATA_CACHE_KEY,
    JSON.stringify({ ...existingCache, ...prefilledCardData }), // merge the latest data with the existing cache
  );
  logger.debug('Saved prefilled card data to local storage', { prefilledCardData });

  // if there was a token in the query string, save it for later
  const opaqueToken = query.opaque_token;
  if (opaqueToken && typeof opaqueToken === 'string') {
    window.localStorage.setItem(OPAQUE_TOKEN_CACHE_KEY, opaqueToken);
    logger.debug('Saved opaque token to local storage');
  }
}

/**
 * Parse the referral data to send to GA4
 * @param query the query parsed from the url
 * @doc https://www.notion.so/blinq-me/Analytics-Improvements-4adad8865da04c1aa4cd06c9dd447c02
 * @returns
 */
export function parseAndCacheReferralTrackingData(
  query: Record<string, string | string[] | undefined>,
): Record<string, string> | undefined {
  const dlTag = query._dl;
  if (Array.isArray(dlTag) || !dlTag) {
    return;
  }

  const existingCache = getCachedReferralTrackingData() ?? {};

  const referralData = Object.fromEntries(
    Object.entries(query)
      .filter(([entryKey]) => entryKey.startsWith('ref_'))
      .map(([key, value]) => {
        const queryValue = Array.isArray(value) ? value[0] : (value ?? '');
        return [key, queryValue];
      }),
  );

  window.localStorage.setItem(
    REFERRAL_DATA_CACHE_KEY,
    JSON.stringify({ ...existingCache, ...referralData }), // merge the latest data with the existing cache
  );

  logger.debug('Saved referral data to local storage', { referralData });
}

/**
 * Get cached referral data to send to GA4
 * @returns cached data or null
 */
export function getCachedReferralTrackingData(): Record<string, string> | null {
  const cachedData = window.localStorage.getItem(REFERRAL_DATA_CACHE_KEY);
  if (cachedData) {
    return JSON.parse(cachedData);
  } else {
    return null;
  }
}

/**
 * Get cached referral data to send to GA4
 * @returns cached data or null
 */
export function getCachedPrefilledCardData(): Record<ReferralDataQueryStringKey, string> | null {
  const cachedData = window.localStorage.getItem(PREFILLED_CARD_DATA_CACHE_KEY);
  if (cachedData) {
    return JSON.parse(cachedData);
  } else {
    return null;
  }
}

/**
 * Send referral data to analytics.
 * This function should be replaced with useAnalytics once the next round of analytics is merged
 * @returns the blinq user id
 */
export function sendReferralDataToAnalytics(blinqUserID: string): void {
  const cachedReferralData = getCachedReferralTrackingData();
  if (!cachedReferralData) return; // return early
  if (
    // only send if there is a referrer
    'ref_card_id' in cachedReferralData ||
    'ref_email_signature_id' in cachedReferralData ||
    'ref_user_id' in cachedReferralData
  ) {
    analytics.track({
      name: 'referred_via_blinq',
      user_id: blinqUserID,
      properties: cachedReferralData,
    });
  }
}
